import { ViewIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import React from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import {
  type ApiError,
  LoginService,
  type UserPublic,
  UsersService,
  type UserUpdate,
} from "../../client"
import useCustomToast from "../../hooks/useCustomToast"
import { emailPattern, handleError } from "../../utils"

interface EditUserProps {
  user: UserPublic
  isOpen: boolean
  onClose: () => void
}

interface UserUpdateForm extends UserUpdate {
  roleCode: string
  roleLevel: string
}

const EditUser = ({ user, isOpen, onClose }: EditUserProps) => {
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const initialRoleCode =
    Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0].code : ""
  const initialLevel =
    Array.isArray(user.roles) && user.roles.length > 0
      ? (user.roles[0].level || "").toLowerCase()
      : ""

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UserUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: user.email,
      fullName: user.fullName ?? "",
      isActive: user.isActive,
      acronym: user.acronym ?? "",
      roleCode: initialRoleCode,
      roleLevel: initialLevel,
    },
  })

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: () => UsersService.readRoles(),
  })

  const rolesByLevel = React.useMemo(() => {
    const grouped: Record<string, any[]> = {}
    ;(roles || []).forEach((r) => {
      const lvl = (r.level || "").toLowerCase()
      if (!grouped[lvl]) grouped[lvl] = []
      grouped[lvl].push(r)
    })
    return grouped
  }, [roles])

  const availableLevels = React.useMemo(
    () => Object.keys(rolesByLevel),
    [rolesByLevel],
  )

  const selectedLevel = watch("roleLevel")

  React.useEffect(() => {
    if (!selectedLevel && availableLevels.length > 0) {
      const first = availableLevels[0]
      setValue("roleLevel", first, { shouldValidate: true })
    }
  }, [availableLevels, selectedLevel, setValue])

  const mutation = useMutation({
    mutationFn: (data: UserUpdateForm) =>
      UsersService.updateUser({
        userId: user.ID,
        requestBody: {
          email: data.email,
          fullName: data.fullName,
          isActive: data.isActive,
          acronym: data.acronym,
          roles: data.roleCode ? [data.roleCode.trim().toUpperCase()] : [],
        },
      }),
    onSuccess: () => {
      showToast("Sukces!", "Użytkownik został zaktualizowany.", "success")
      onClose()
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => LoginService.recoverPassword({ email }),
    onSuccess: () => {
      showToast(
        "Sukces!",
        "Wysłano wiadomość e-mail do resetu hasła.",
        "success",
      )
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<UserUpdateForm> = async (data) => {
    mutation.mutate(data)
  }

  const onCancel = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "sm", md: "md" }}
      isCentered
    >
      <ModalOverlay bg="blackAlpha.400" _dark={{ bg: "blackAlpha.700" }} />
      <ModalContent
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        bg="bg.table"
        color="text.input"
      >
        <ModalHeader borderBottomWidth="1px" borderBottomColor="border.table">
          Edytuj użytkownika
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <FormControl isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input
              id="email"
              {...register("email", {
                required: "Adres e-mail jest wymagany",
                pattern: emailPattern,
              })}
              placeholder="Email"
              type="email"
              bg="bg.input"
              color="text.input"
              borderColor="border.table"
            />
            {errors.email && (
              <FormErrorMessage>{errors.email.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl mt={4}>
            <FormLabel htmlFor="name">Imię i nazwisko</FormLabel>
            <Input
              id="name"
              {...register("fullName")}
              type="text"
              bg="bg.input"
              color="text.input"
              borderColor="border.table"
            />
          </FormControl>
          <FormControl mt={4}>
            <FormLabel htmlFor="roleLevel">Poziom</FormLabel>
            {rolesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">Ładowanie poziomów...</Text>
              </Flex>
            ) : (
              <Select
                id="roleLevel"
                placeholder="Wybierz poziom"
                value={selectedLevel}
                onChange={(e) => {
                  setValue("roleLevel", e.target.value, {
                    shouldValidate: true,
                  })
                }}
                bg="bg.input"
                color="text.input"
                borderColor="border.table"
              >
                {availableLevels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </Select>
            )}
            <FormHelperText>
              Najpierw wybierz poziom (user/admin/superadmin).
            </FormHelperText>
          </FormControl>

          <FormControl mt={4} isRequired isInvalid={!!errors.roleCode}>
            <FormLabel htmlFor="roleCode">Rola</FormLabel>
            {rolesLoading ? (
              <Flex align="center" gap={2}>
                <Spinner size="sm" />
                <Text fontSize="sm">Ładowanie ról...</Text>
              </Flex>
            ) : (
              <Controller
                control={control}
                name="roleCode"
                rules={{
                  required: "Rola jest wymagana",
                  validate: () => {
                    if (!selectedLevel) return "Najpierw wybierz poziom"
                    return true
                  },
                }}
                render={({ field }) => {
                  const levelRoles = selectedLevel
                    ? rolesByLevel[selectedLevel] || []
                    : []
                  return (
                    <Stack spacing={2}>
                      {levelRoles.length === 0 && (
                        <Text fontSize="sm" color="gray.500">
                          Brak ról dla wybranego poziomu.
                        </Text>
                      )}
                      {levelRoles.map((role: any) => (
                        <Box
                          key={role.code}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          cursor="pointer"
                          bg={
                            field.value === role.code
                              ? "main.accent"
                              : "transparent"
                          }
                          borderColor={
                            field.value === role.code
                              ? "main.accent"
                              : "border.table"
                          }
                          color={
                            field.value === role.code ? "white" : "text.input"
                          }
                          onClick={() => field.onChange(role.code)}
                          _hover={{ borderColor: "main.accent" }}
                        >
                          <Flex align="center" justify="space-between">
                            <Box>
                              <Text fontWeight="medium">{role.code}</Text>
                              <Text
                                fontSize="xs"
                                color={
                                  field.value === role.code
                                    ? "whiteAlpha.800"
                                    : "gray.500"
                                }
                                _dark={{ color: "gray.400" }}
                              >
                                Poziom: {role.level}
                              </Text>
                            </Box>
                            {field.value === role.code && (
                              <Box color="white">
                                <ViewIcon />
                              </Box>
                            )}
                          </Flex>
                        </Box>
                      ))}
                    </Stack>
                  )
                }}
              />
            )}
            <FormHelperText>Wybierz rolę dla wybranego poziomu.</FormHelperText>
            {errors.roleCode && (
              <FormErrorMessage>
                {errors.roleCode.message as string}
              </FormErrorMessage>
            )}
          </FormControl>
          <Flex mt={4}>
            <FormControl>
              <Checkbox {...register("isActive")} colorScheme="green">
                Użytkownik aktywny
              </Checkbox>
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter gap={3} borderTopWidth="1px" borderTopColor="border.table">
          <Button
            variant="outline"
            onClick={() => resetPasswordMutation.mutate(user.email)}
            isLoading={resetPasswordMutation.isPending}
            isDisabled={!user.email}
          >
            Wyślij e-mail do resetu
          </Button>
          <Button
            variant="primary"
            type="submit"
            isLoading={isSubmitting}
            isDisabled={!isDirty}
            _disabled={{
              bg: "main.accent",
              color: "white",
              opacity: 0.7,
              cursor: "not-allowed",
              _hover: {
                bg: "main.accent",
              },
            }}
          >
            Zapisz
          </Button>
          <Button onClick={onCancel}>Anuluj</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default EditUser
